import { Box } from '@moonpig/launchpad-components'
import React, { FC } from 'react'
import { breakpointUp, styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { Carousel } from '@moonpig/web-shared-components'
import { DepartmentsEnum } from '@moonpig/web-shared-types-graphql'
import { getParentDepartment } from '@moonpig/web-core-utils'
import { spacingPx } from '@moonpig/launchpad-theme'
import { useSearchStore } from '../../store/SearchStore'
import { DropDownMenu } from './DropDownMenu'
import { FilterCategory, FilterToggle } from '../../services/types/services'
import { QuickFilterToggle } from './QuickFilterToggle/QuickFilterToggle'
import { QuickSortBy } from './QuickSortBy/QuickSortBy'
import { DesktopQuickSortBy } from './QuickSortBy/DesktopQuickSortBy'
import { orderQuickFilters } from './orderQuickFilters'
import { FiltersMenuButton } from '../FiltersCarousel/components'
import { ClearButton } from '../ClearButton'
import { FilterSource } from '../../types'

const StyledQuickFiltersBar = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: ${spacingPx(6)};

  ${breakpointUp('md')} {
    overflow-x: scroll;
    overscroll-behavior: contain;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  ${s({
    bgcolor: 'colorBackground01',
    borderBottom: 1,
    borderColor: 'colorBorder03',
  })}
`

const StyledCarousel = styled(Carousel)`
  ${s({
    width: '100%',
    pl: { xs: 0, md: 8, lg: 10, xxl: 12 },
  })}
`

export const QuickFilters: FC<{ departments: DepartmentsEnum[] }> = ({
  departments,
}) => {
  const selectedFilters = useSearchStore(store => store.selectedFilters)
  const toggleFiltersMenu = useSearchStore(store => store.toggleFiltersMenu)
  const filters = useSearchStore(store => store.filters)
  const toggleFilter = useSearchStore(store => store.toggleFilter)
  const filtersMenuOpen = useSearchStore(store => store.filtersMenuOpen)
  const resultsLoading = useSearchStore(store => store.loading)
  const results = useSearchStore(store => store.results)
  const toggleFiltersCategory = useSearchStore(
    store => store.toggleFiltersCategory,
  )
  const applyRangeFilter = useSearchStore(store => store.applyRangeFilter)
  const onRangeFilterChange = useSearchStore(store => store.onRangeFilterChange)
  const rangeFilters = useSearchStore(store => store.rangeFilters)
  const clearFilters = useSearchStore(store => store.clearFilters)

  const department = getParentDepartment(departments[0])
  const orderedFilters = orderQuickFilters(filters, department)

  return (
    <StyledQuickFiltersBar data-testid="dropdown-filters">
      <StyledCarousel itemsGap={{ xs: 0 }} noSpacing alignItems="center">
        <FiltersMenuButton
          toggleMenu={toggleFiltersMenu}
          isMenuOpen={filtersMenuOpen}
          appliedFilters={selectedFilters}
          noPadding
        />
        {(
          orderedFilters.filter(
            f => f.__typename === 'FilterCategory',
          ) as FilterCategory[]
        ).map(filterCategory => {
          return (
            <DropDownMenu
              filterCategory={filterCategory}
              selectedFilters={selectedFilters}
              key={`${filterCategory.id}-quick-filter`}
              onChange={() => toggleFiltersCategory(filterCategory)}
              toggleFilter={toggleFilter}
              toggleFilterCategory={toggleFiltersCategory}
              onRangeFilterChange={onRangeFilterChange}
              rangeFilters={rangeFilters}
              applyRangeFilter={applyRangeFilter}
              count={results.totalCount}
              loading={resultsLoading}
              clearFilters={clearFilters}
            />
          )
        })}
        {(
          orderedFilters.filter(
            f =>
              f.__typename === 'FilterToggle' ||
              f.__typename === 'FilterInvertedToggle',
          ) as FilterToggle[]
        ).map(filter => {
          return (
            <QuickFilterToggle
              toggleFilter={filter}
              key={`${filter.id}-quick-filter-toggle`}
            />
          )
        })}
        <Box
          display={{ xs: 'flex', md: 'flex', lg: 'none' }}
          alignItems="center"
          data-testid="mobile-quick-sort-by"
        >
          <QuickSortBy onClick={() => toggleFiltersMenu('sort')} />
        </Box>
        <ClearButton
          level={0}
          source={FilterSource.QuickFilters}
          hide={selectedFilters.length === 0}
        />
      </StyledCarousel>
      <DesktopQuickSortBy onClick={() => toggleFiltersMenu('sort')} />
    </StyledQuickFiltersBar>
  )
}
